/* eslint-disable react/prop-types */
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useEffectAsync } from "lib/react/customHooks";
import { Grid, useMediaQuery, makeStyles, Typography } from "@material-ui/core";
import { nFormatter } from "lib/utils";
import _ from "lodash";
import CardComponent from "app/common/CardComponent";
import getElasticities from "lib/requests/elasticities";
import moment from "moment";
import ElasticitySankeyChartComponent from "./ElasticitySankeyChartComponent";
import ExplicabilityComponent from "../../explicability-info/ExplicabilityComponent";
import SelectComponent from "../SelectComponent";
import { setTupleData } from "../duck/slices";

const useStyles = makeStyles((theme) => ({
  aggregateContainer: {
    display: "flex",
    alignItems: "baseline",
    [theme.breakpoints.up("xs")]: {
      marginLeft: "20px",
    },
    [theme.breakpoints.up("lg")]: {
      marginLeft: "5px",
    },
  },
  chartContainer: {
    position: "relative",
    maxHeight: "100%",
    overflow: "hidden",
    flexGrow: "1",
    [theme.breakpoints.up("xs")]: {
      height: "155px",
    },
    [theme.breakpoints.up("sm")]: {
      height: "200px",
      marginTop: "0px",
    },
    [theme.breakpoints.up("md")]: {
      height: "300px",
      marginTop: "20px",
    },
    [theme.breakpoints.up("lg")]: {
      height: "100%",
    },
  },
  elasticityGain: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.up("xs")]: {
      marginBottom: "10px",
      justifyContent: "space-between",
    },
    [theme.breakpoints.up("lg")]: {
      justifyContent: "flex-end",
      paddingRight: "20px",
    },
  },
  elasticityGainContent: {
    display: "flex",
    [theme.breakpoints.up("xs")]: {
      alignItems: "flex-end",
    },
    [theme.breakpoints.up("md")]: {
      flexDirection: "column",
      alignItems: "start",
    },
    "& > p": {
      [theme.breakpoints.up("xs")]: {
        marginRight: "5px",
      },
    },
  },
  componentTitle: {
    fontWeight: "bold",
    fontSize: "14px",
    [theme.breakpoints.up("xl")]: {
      fontSize: "16px",
    },
  },
}));

function ElasticitySankeyComponent({ filtersKey, queryFilters }) {
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const classes = useStyles();
  const dispatch = useDispatch();
  const filtersSlice = useSelector((state) => state.filters);
  const elasticitiesSlice = useSelector((state) => state.elasticities);

  const [elasticityData, setElasticityData] = React.useState(null);
  const [priceChangeDirection, setPriceChangeDirection] = React.useState({
    key: "decrease",
    label: "Discount",
  });

  const selectOptions = [
    {
      key: "decrease",
      label: "Discount",
    },
    {
      key: "increase",
      label: "Increase",
    },
  ];

  const retrieveElasticityForTuple = async (tupleKey) => {
    if (elasticitiesSlice.tuples[tupleKey]?.sankeyDataOwnProducts) {
      return {
        key: tupleKey,
        sankeyDataOwnProducts:
          elasticitiesSlice.tuples[tupleKey].sankeyDataOwnProducts,
        sankeyDataCompetitorsProducts:
          elasticitiesSlice.tuples[tupleKey].sankeyDataCompetitorsProducts,
      };
    }

    const requestQueryFilters = {
      ...queryFilters,
    };
    // Remove product_id from filters because we want to use it as source_product_id
    const productId = requestQueryFilters.product_id;
    delete requestQueryFilters.product_id;

    const baseFilters = {
      ...requestQueryFilters,
      is_source_product_client: 1,
      data_source_id: 1,
      filter_type: "flexible",
      min_date: moment()
        .startOf("month")
        .format("YYYY-MM-DD"),
      max_date: moment()
        .add(1, "years")
        .endOf("month")
        .format("YYYY-MM-DD"),
    };

    // OWN PRODUCTS //
    const sankeyOwnProductsResult = await getElasticities({
      filters: {
        ...baseFilters,
        data_source_id: 1,
        ...(productId && { source_product_id: productId }),
      },
      group_by: ["product_id", "source_product_id"],
      join_by: "source",
      sum: ["constant_increase", "constant_decrease"],
    });

    // COMPETITOR PRODUCTS //
    const sankeyDataCompetitorsProductsResult = await getElasticities({
      filters: {
        ...baseFilters,
        is_product_client: 0,
        data_source_id: 2,
        ...(productId && { source_product_id: productId }),
      },
      group_by: ["product_id", "source_product_id"],
      join_by: "source",
      sum: ["constant_increase", "constant_decrease"],
    });

    return {
      key: tupleKey,
      sankeyDataOwnProducts: sankeyOwnProductsResult.data,
      sankeyDataCompetitorsProducts: sankeyDataCompetitorsProductsResult.data,
    };
  };

  useEffectAsync(
    async () => {
      const tupleKey = `sankey-${filtersKey}`;

      return retrieveElasticityForTuple(tupleKey);
    },
    (result) => {
      // For each product im only interested in it's own elasticity
      const clientOwnElasticityData = _.filter(
        result.sankeyDataOwnProducts,
        (elasticityTuple) => {
          return (
            elasticityTuple.product_id === elasticityTuple.source_product_id &&
            elasticityTuple.sum_constant_decrease !== null &&
            elasticityTuple.sum_constant_increase !== null
          );
        }
      );

      // For each product I filter it's cross elasticity effects with our own products
      const clientCrossElasticityData = _.filter(
        result.sankeyDataOwnProducts,
        (elasticityTuple) => {
          return (
            elasticityTuple.product_id !== elasticityTuple.source_product_id &&
            elasticityTuple.sum_constant_decrease !== null &&
            elasticityTuple.sum_constant_increase !== null
          );
        }
      );

      // For each product I filter it's cross elasticity effects with competitor products where I am the source
      const competitorsCrossElasticityData = _.filter(
        result.sankeyDataCompetitorsProducts,
        (elasticityTuple) => {
          return (
            elasticityTuple.product_id !== elasticityTuple.source_product_id &&
            elasticityTuple.sum_constant_decrease !== null &&
            elasticityTuple.sum_constant_increase !== null
          );
        }
      );

      const aggregatedClientCrossElasticityData = _.sumBy(
        clientCrossElasticityData,
        `sum_constant_${priceChangeDirection.key}`
      );

      const aggregatedClientOwnElasticityData = _.sumBy(
        clientOwnElasticityData,
        `sum_constant_${priceChangeDirection.key}`
      );

      const aggregatedCompetitorsCrossElasticityData = _.sumBy(
        competitorsCrossElasticityData,
        `sum_constant_${priceChangeDirection.key}`
      );

      const aggregatedCompetitorsAndAdditionalEffect =
        Math.abs(aggregatedClientOwnElasticityData) -
        Math.abs(aggregatedClientCrossElasticityData);

      const aggregatedCannibalizationEffect = Math.abs(
        aggregatedClientCrossElasticityData
      );

      const aggregatedAdditionalNet =
        aggregatedCompetitorsAndAdditionalEffect -
        Math.abs(aggregatedCompetitorsCrossElasticityData);

      const tupleData = {
        clientOwnElasticityData,
        clientCrossElasticityData,
        competitorsCrossElasticityData,
        aggregatedClientOwnElasticityData,
        aggregatedClientCrossElasticityData,
        aggregatedCompetitorsCrossElasticityData,
        aggregatedCompetitorsAndAdditionalEffect,
        aggregatedCannibalizationEffect,
        aggregatedAdditionalNet,
      };

      dispatch(
        setTupleData({
          [result.key]: {
            ...elasticitiesSlice.tuples[result.key],
            sankeyDataOwnProducts: result.sankeyDataOwnProducts,
            sankeyDataCompetitorsProducts: result.sankeyDataCompetitorsProducts,
          },
        })
      );

      setElasticityData(tupleData);
    },
    [filtersSlice.filters, priceChangeDirection]
  );

  return (
    <>
      {elasticityData &&
        _.some(
          [
            ...elasticityData.clientOwnElasticityData,
            ...elasticityData.competitorsCrossElasticityData,
            ...elasticityData.clientCrossElasticityData,
          ],
          (item) => {
            return (
              item.sum_constant_decrease !== null ||
              item.sum_constant_increase !== null
            );
          }
        ) && (
          <CardComponent
            title={
              <>
                <Typography variant="body1" className={classes.componentTitle}>
                  Cross Effects
                  <ExplicabilityComponent tooltipText="See how volume  is distributed when you increase or decrease a 1%  your prices thanks to predictive cross and own  elasticities. Visualize how the cross effects affect your products (cannibalization effects)  and your competitors (competitors effects)" />
                </Typography>
                <SelectComponent
                  selectValue={priceChangeDirection}
                  setSelectValue={setPriceChangeDirection}
                  selectTitle="Price Change"
                  selectOptions={selectOptions}
                />
                {!isSmallScreen && (
                  <div className={classes.aggregateContainer}>
                    <Typography variant="caption">
                      {priceChangeDirection.key === "decrease"
                        ? "Net Gain"
                        : "Net Loss"}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      style={{
                        fontWeight: "bold",
                        marginLeft: "10px",
                      }}
                    >
                      {nFormatter(
                        elasticityData.aggregatedClientOwnElasticityData +
                          elasticityData.aggregatedClientCrossElasticityData
                      )}
                    </Typography>
                  </div>
                )}
              </>
            }
            body={
              <Grid container className={classes.chartContainer}>
                <Grid
                  className={classes.elasticityGain}
                  item
                  lg={2}
                  md={2}
                  sm={12}
                  xs={12}
                >
                  <div className={classes.elasticityGainContent}>
                    <Typography variant="body2">
                      {priceChangeDirection.key === "decrease"
                        ? "Own Elasticity Gain"
                        : "Own Elasticity Loss"}
                    </Typography>
                    <Typography variant="caption">
                      {nFormatter(
                        elasticityData.aggregatedClientOwnElasticityData
                      )}
                    </Typography>
                  </div>
                  {isSmallScreen && (
                    <div className={classes.aggregateContainer}>
                      <Typography variant="caption">
                        {priceChangeDirection.key === "decrease"
                          ? "Net Gain"
                          : "Net Loss"}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        style={{
                          fontWeight: "bold",
                          marginLeft: "10px",
                        }}
                      >
                        {nFormatter(
                          elasticityData.aggregatedClientOwnElasticityData +
                            elasticityData.aggregatedClientCrossElasticityData
                        )}
                      </Typography>
                    </div>
                  )}
                </Grid>
                <Grid
                  item
                  lg={9}
                  md={9}
                  sm={12}
                  xs={12}
                  style={{ overflow: "hidden" }}
                >
                  <ElasticitySankeyChartComponent
                    priceChangeDirection={priceChangeDirection}
                    data={elasticityData}
                  />
                </Grid>
              </Grid>
            }
          />
        )}
    </>
  );
}

export default ElasticitySankeyComponent;
